import { combineLatest, Observable, of, ReplaySubject } from 'rxjs';
import { catchError, map, startWith, switchMap } from 'rxjs/operators';

import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Input, Optional } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';

import { TranslateService } from '@ngx-translate/core';

import { Store } from '@ngxs/store';

import { CommonAPI } from '@app/core/api/common.service';
import { ApiErrorResponse } from '@app/core/models/api.models';
import { countries, Country, isCountryInEu } from '@app/core/models/countries.model';
import { Customer, InvoiceLanguage } from '@app/core/models/customer.models';
import { UpdateCustomer } from '@app/core/store/states/customer/customer-state.actions';
import { assertArray } from '@app/core/utilities/array.utilities';
import { snakeToCamelCase } from '@app/core/utilities/string.utilities';
import { lastValue } from '@app/shared/operators/last-value.operator';
import { shareRef } from '@app/shared/operators/share-ref.operator';
import { environment } from '@env/environment';
import { NZ_MODAL_DATA } from 'ng-zorro-antd/modal';

export function invalidValueValidator(value: string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const invalid = value === control.value;
    return invalid ? { invalidValue: { value: control.value } } : null;
  };
}

export function vatRequiredValidator(control: AbstractControl): ValidationErrors | null {
  return isCountryInEu(control.get('code')?.value) && !control.get('vatId')?.value?.trim()
    ? { vatRequired: true }
    : null;
}

@Component({
  standalone: false,
  selector: 'adspt-billing-form',
  templateUrl: './billing-form.component.html',
  styleUrls: ['./billing-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BillingFormComponent {
  private customer$ = new ReplaySubject<Customer>(1);

  readonly countries = countries;

  @Input()
  set billingDetails(customer: Customer) {
    this.customer$.next(customer);
  }

  @Input()
  newCustomer?: boolean;

  readonly invoiceLanguages: Record<InvoiceLanguage, string> = {
    EN: 'English',
    DE: 'Deutsch',
  };

  readonly languageSelectOptions = Object.entries(this.invoiceLanguages).map(([value, text]) => ({ value, text }));

  readonly flagsUrl = environment.app.countryFlagUrl;

  readonly billingForm$: Observable<FormGroup> = combineLatest([
    this.customer$,
    this.tl.onLangChange.pipe(
      startWith(this.tl.currentLang),
      map((lang) => lang?.toString().toLocaleUpperCase() || 'EN'),
      map((lang) => (lang === 'ZH' ? 'EN' : lang)),
    ),
  ]).pipe(
    map(([details, defaultInvoiceLanguage]) => {
      const form = this.fb.group({
        name: [details.name, [Validators.required]],
        email: [details.email, [Validators.required, Validators.email]],
        address1: [details.address1, [Validators.required]],
        address2: [details.address2],
        zip: [details.zip, [Validators.required]],
        city: [details.city, [Validators.required]],
        country: this.fb.group(
          {
            code: [details.countryCode, [Validators.required]],
            vatId: [
              details.vatId,
              details.vatId && details.vatIdValid === false ? [invalidValueValidator(details.vatId)] : [],
            ],
          },
          { validators: vatRequiredValidator },
        ),
        invoiceLanguage: [details.invoiceLanguage || defaultInvoiceLanguage],
        orderNumber: [details.orderNumber],
      });
      if (details.vatId && details.vatIdValid === false) {
        form.get('country.vatId').markAsDirty();
        form.get('country.vatId').updateValueAndValidity({ onlySelf: true });
      }
      return form;
    }),
    shareRef(),
  );

  readonly vatRequired$: Observable<boolean> = this.billingForm$.pipe(
    switchMap(
      (form) => form.get('country.code')?.valueChanges.pipe(startWith(form.get('country.code')?.value)) || of(null),
    ),
    map((countryCode) => isCountryInEu(countryCode)),
    shareRef(),
  );

  get form$(): Observable<FormGroup> {
    return this.billingForm$;
  }

  constructor(
    private tl: TranslateService,
    private fb: FormBuilder,
    private ca: CommonAPI,
    private store: Store,
    private cd: ChangeDetectorRef,
    @Optional() @Inject(NZ_MODAL_DATA) data: Partial<BillingFormComponent>,
  ) {
    Object.keys(data || {}).forEach((key) => (this[key] = data[key]));
  }

  submitForm(): Observable<boolean> {
    // this.billingFormGroup.onSubmit({} as Event);
    const form: FormGroup = lastValue(this.billingForm$);
    const details = form.value && { ...form.value };

    if (!details || !form.valid) {
      Object.values(form.controls).forEach((control) => {
        if (control.invalid) {
          control.markAsDirty();
          control.updateValueAndValidity({ onlySelf: true });
        }
      });

      return of(false);
    }

    details.countryCode = details.country?.code;
    details.vatId = details.country?.vatId;

    delete details.country;

    return this.store.dispatch(new UpdateCustomer(details)).pipe(
      map(() => true),
      catchError((error: ApiErrorResponse) => {
        const errors = (error && error.status === 422 && assertArray(error.errors)) || [];

        errors.forEach(({ path }) => {
          const control = form.get(path.map((item) => snakeToCamelCase(item.toString())));

          if (control) {
            control.setErrors({ invalid: true });
          }
        });

        this.cd.markForCheck();

        return of(false);
      }),
    );
  }

  getCountryFromValue(value: string): Country {
    return countries.find((country) => country.value === value);
  }
}
